import {Typography, css, useTheme} from '@mui/material';
import {DlMuiChip, GetStylesFunction, isNullsy, StringUtils} from '@wallaby-ui/core';
import * as React from 'react';

import {LightTable, LTColumn} from '../../../common/components/LightTable';
import {TableSorting} from '../../../common/components/MaterialTableWrapper';
import {StructuredMedicationRowDto, useLoadStructuredMedications} from '../hooks/useLoadStructuredMedications';
import {Formatters, TooltipWrapper, resolveMedicationUsageTypeChip, useFormatters} from 'src/js/common';

interface OwnProps {
  documentVersionId: number | string | undefined;
}

/* ------------------------------------------------------------------ */
/* Value formatters                                                    */
/* ------------------------------------------------------------------ */

function formatRouteOfAdministration(route: StructuredMedicationRowDto['routeOfAdministration'], formatters: Formatters): string {
  const {t, tEnum} = formatters;
  if (!route || route === 'UNKNOWN') return t('global.unknown');
  const translated = tEnum('PatientMedicationRouteOfAdministrationTypeEnum', route);
  if (!translated || translated.startsWith('enum.')) return route;
  return translated;
}

function formatPrescribedAt(row: StructuredMedicationRowDto, formatters: Formatters): string | null {
  const {formatDateTimeShort} = formatters;
  return row.prescribedAt ? formatDateTimeShort(row.prescribedAt) : null;
}

function formatAdministeredAt(row: StructuredMedicationRowDto, formatters: Formatters): string | null {
  const {formatDateTimeShort} = formatters;
  const at = row.administeredAt ? formatDateTimeShort(row.administeredAt) : null;
  const until = row.administeredUntil ? formatDateTimeShort(row.administeredUntil) : null;
  if (at && until && at !== until) return `${at} - ${until}`;
  return at;
}

/* ------------------------------------------------------------------ */
/* Column visibility                                                   */
/* ------------------------------------------------------------------ */

const isStringPresent = (v: string | null | undefined) => Boolean(v && v.length > 0);
const isListPresent = (v: string[] | undefined | null) => Array.isArray(v) && v.length > 0;
const isEnumKnown = (v: string | null | undefined) => Boolean(v && v !== 'UNKNOWN');

/** Per-column visibility check — runs once after data load. Medication name
    is never hidden (it identifies the row). */
type ColumnId =
  | 'medicationName'
  | 'usageType'
  | 'strength'
  | 'routeOfAdministration'
  | 'dosage'
  | 'comment'
  | 'atcGroupLabels'
  | 'activeIngredientLabels';

const COLUMN_HAS_DATA: Record<ColumnId, (rows: StructuredMedicationRowDto[]) => boolean> = {
  medicationName: () => true,
  usageType: (rows) => rows.some((r) => isEnumKnown(r.usageType)) || rows.some((r) => r.isDiscontinued),
  strength: (rows) => rows.some((r) => isStringPresent(r.medicationStrength)),
  routeOfAdministration: (rows) => rows.some((r) => isEnumKnown(r.routeOfAdministration)),
  dosage: (rows) => rows.some((r) => isStringPresent(r.dosage)),
  comment: (rows) => rows.some((r) => isStringPresent(r.comment)),
  atcGroupLabels: (rows) => rows.some((r) => isListPresent(r.atcGroupLabels)),
  activeIngredientLabels: (rows) => rows.some((r) => isListPresent(r.activeIngredientLabels)),
};

/* ------------------------------------------------------------------ */
/* Tooltip only for cut-off text                                       */
/* ------------------------------------------------------------------ */

/** True when the element, or one of its children (stacked list items),
    is cut off by its ellipsis. */
const isTruncated = (element: HTMLElement): boolean =>
  [element, ...Array.from(element.children)].some((e) => e.scrollWidth > e.clientWidth);

interface TooltipIfTruncatedProps {
  tooltip: string | JSX.Element;
  /** Changes whenever the rendered text changes, so the cell is re-measured. */
  contentKey: string;
  render: (measureRef: (element: HTMLElement | null) => void) => JSX.Element;
}

/** Cell content that shows its tooltip only when the text does not fit the
    column. The TooltipWrapper is rendered either way — its vertical padding
    keeps every text cell the same height, so all cells line up in the row. */
const TooltipIfTruncated: React.FC<TooltipIfTruncatedProps> = (props) => {
  const {tooltip, contentKey, render} = props;
  const [element, setElement] = React.useState<HTMLElement | null>(null);
  const [truncated, setTruncated] = React.useState(false);

  React.useLayoutEffect(() => {
    if (!element) return undefined;
    const measure = () => setTruncated(isTruncated(element));
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [element, contentKey]);

  return (
    <TooltipWrapper tooltip={truncated ? tooltip : undefined} maxWidth={400}>
      {render(setElement)}
    </TooltipWrapper>
  );
};

/* ------------------------------------------------------------------ */
/* Sort comparators                                                    */
/* ------------------------------------------------------------------ */

const cmpString = (a: string | null | undefined, b: string | null | undefined): number => {
  const sa = a ?? '';
  const sb = b ?? '';
  return StringUtils.compare(sa, sb);
};
const cmpStringList = (a: string[] | undefined, b: string[] | undefined): number => cmpString((a ?? []).join('; '), (b ?? []).join('; '));
const cmpDate = (a: string | null | undefined, b: string | null | undefined): number => {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return a.localeCompare(b);
};

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

const DEFAULT_SORTING: TableSorting = {field: 'medicationName', direction: 'asc'};

export const MedicationPlaceholderDocument: React.FC<OwnProps> = (props) => {
  const {documentVersionId} = props;
  const theme = useTheme();
  const styles = getStyles({theme});
  const formatters = useFormatters();
  const {t} = formatters;
  const {data, isLoading, isError} = useLoadStructuredMedications({documentVersionId});

  const [sorting, setSorting] = React.useState<TableSorting>(DEFAULT_SORTING);

  if (isError) {
    return (
      <Typography variant={'value'} css={styles.message}>
        {t('global.unknown')}
      </Typography>
    );
  }

  const medications = data?.medications ?? [];
  const unknownText = t('global.unknown');

  /* ----- per-row rendering helpers ----------------------------------- */

  // Use the MUI custom `coolGray` palette (shared by HAIDI + MERIE — HAIDI's
  // theme imports it from merieColors). The previous --dlThemeGray-600 CSS var
  // rendered very differently between apps (HAIDI #9BA8BD vs MERIE #4D5358,
  // making MERIE discontinued rows look like normal text).
  const grayIfDiscontinued = (isDiscontinued: boolean): React.CSSProperties =>
    isDiscontinued ? {color: theme.palette.custom.coolGray['400']} : {};

  // Every text cell goes through TooltipIfTruncated: the tooltip appears only
  // when the text is cut off, and the shared wrapper keeps the cells aligned.
  const renderText = (row: StructuredMedicationRowDto, value: string | null | undefined, fallback: string = unknownText) => {
    const display = isStringPresent(value) ? value! : fallback;
    return (
      <TooltipIfTruncated
        tooltip={display}
        contentKey={display}
        render={(measureRef) => (
          <Typography ref={measureRef} variant={'value'} css={styles.ellipsisCell} style={grayIfDiscontinued(row.isDiscontinued)}>
            {display}
          </Typography>
        )}
      />
    );
  };

  const renderList = (row: StructuredMedicationRowDto, items: string[] | undefined) => {
    if (!isListPresent(items)) return renderText(row, null);
    // Tooltip stacks each item on its own line, matching the cell layout.
    const tooltipContent = (
      <div css={styles.tooltipStack}>
        {items!.map((v, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <Typography key={i} variant={'value'}>
            {v}
          </Typography>
        ))}
      </div>
    );
    return (
      <TooltipIfTruncated
        tooltip={tooltipContent}
        contentKey={items!.join('\n')}
        render={(measureRef) => (
          <div ref={measureRef} css={styles.stackedList}>
            {items!.map((v, i) => (
              <Typography
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                variant={'value'}
                css={styles.ellipsisCell}
                style={grayIfDiscontinued(row.isDiscontinued)}
              >
                {v}
              </Typography>
            ))}
          </div>
        )}
      />
    );
  };

  // `forceShow: true` guarantees a chip descriptor (no null) — UNKNOWN
  // renders as a light-gray "Unknown" chip rather than nothing, since the
  // column is otherwise hidden by COLUMN_HAS_DATA.
  // `discontinuedOverridesUsageType: true` — a discontinued medication shows
  // the "Discontinued" (Vysazená) chip in the regime column instead of its
  // raw usageType (e.g. ACUTE).
  const resolveUsageTypeChip = (row: StructuredMedicationRowDto) =>
    resolveMedicationUsageTypeChip({
      usageType: row.usageType,
      isDiscontinued: row.isDiscontinued,
      formatters,
      theme,
      forceShow: true,
      discontinuedOverridesUsageType: true,
    })!;

  // Sort by the *displayed* chip label (translated, with the discontinued
  // override applied), not the raw `usageType` enum — otherwise discontinued
  // rows (all shown as "Vysazená") get scattered by their hidden usageType and
  // the column appears unsorted.
  const usageTypeSortValue = (row: StructuredMedicationRowDto): string => resolveUsageTypeChip(row).label;

  const renderUsageTypeChip = (row: StructuredMedicationRowDto): JSX.Element => {
    const chip = resolveUsageTypeChip(row);
    return <DlMuiChip label={chip.label} style={{backgroundColor: chip.backgroundColor}} whiteText={chip.whiteText} />;
  };

  const renderMedicationNameTooltip = (row: StructuredMedicationRowDto): JSX.Element => {
    const prescribedAt = formatPrescribedAt(row, formatters);
    const administeredAt = formatAdministeredAt(row, formatters);
    return (
      <div css={styles.tooltipWrapper}>
        <div css={styles.tooltipSection}>
          <Typography variant={'value'}>{row.medicationName || unknownText}</Typography>
        </div>
        {row.isDiscontinued && (
          <div css={styles.tooltipSection}>
            <DlMuiChip
              label={t('timeline.annotationTooltip.usageType.isDiscontinued')}
              style={{backgroundColor: theme.palette.custom.coolGray['600']}}
              whiteText
            />
          </div>
        )}
        <div css={styles.tooltipSection}>
          <Typography variant={'labelBold'}>{t('medicationPlaceholder.column.timeOfPrescription')}</Typography>
          <Typography variant={'value'}>{prescribedAt ?? administeredAt ?? unknownText}</Typography>
        </div>
      </div>
    );
  };

  const renderMedicationNameCell = (row: StructuredMedicationRowDto) => (
    <TooltipWrapper tooltip={renderMedicationNameTooltip(row)} maxWidth={300}>
      <Typography variant={'value'} css={styles.ellipsisCell} style={grayIfDiscontinued(row.isDiscontinued)}>
        {row.medicationName || unknownText}
      </Typography>
    </TooltipWrapper>
  );

  /* ----- column definitions ------------------------------------------ */

  const allColumns: (LTColumn<StructuredMedicationRowDto> & {columnId: ColumnId})[] = [
    {
      columnId: 'medicationName',
      field: 'medicationName',
      title: t('hospitalization.detail.phRec.drp.medicationName'),
      width: 240,
      sorting: true,
      customSort: (a, b) => cmpString(a.medicationName, b.medicationName),
      render: (row) => renderMedicationNameCell(row),
    },
    {
      columnId: 'usageType',
      field: 'usageType',
      title: t('medicationPlaceholder.column.usageType'),
      width: 170,
      sorting: true,
      customSort: (a, b) => cmpString(usageTypeSortValue(a), usageTypeSortValue(b)),
      render: (row) => renderUsageTypeChip(row),
    },
    {
      columnId: 'strength',
      field: 'strength',
      title: t('medicationPlaceholder.column.strength'),
      width: 100,
      sorting: true,
      customSort: (a, b) => cmpString(a.medicationStrength, b.medicationStrength),
      render: (row) => renderText(row, row.medicationStrength),
    },
    {
      columnId: 'routeOfAdministration',
      field: 'routeOfAdministration',
      title: t('timeline.annotationTooltip.routeOfAdministration'),
      width: 140,
      sorting: true,
      customSort: (a, b) => cmpString(a.routeOfAdministration, b.routeOfAdministration),
      render: (row) => renderText(row, formatRouteOfAdministration(row.routeOfAdministration, formatters)),
    },
    {
      columnId: 'dosage',
      field: 'dosage',
      title: t('timeline.annotationTooltip.dosage'),
      width: 120,
      sorting: true,
      customSort: (a, b) => cmpString(a.dosage, b.dosage),
      render: (row) => renderText(row, row.dosage),
    },
    {
      columnId: 'comment',
      field: 'comment',
      title: t('timeline.annotationTooltip.note'),
      width: 200,
      sorting: true,
      customSort: (a, b) => cmpString(a.comment, b.comment),
      // Empty note: render an empty cell (no text, no tooltip) instead of the
      // "No note" fallback. The fallback is reserved for the medication-name
      // tooltip etc. — in the table itself an empty Note column is more
      // readable.
      render: (row) => (isStringPresent(row.comment) ? renderText(row, row.comment, '') : <span />),
    },
    {
      columnId: 'atcGroupLabels',
      field: 'atcGroupLabels',
      title: t('timeline.annotationTooltip.atc'),
      width: 220,
      sorting: true,
      customSort: (a, b) => cmpStringList(a.atcGroupLabels, b.atcGroupLabels),
      render: (row) => renderList(row, row.atcGroupLabels),
    },
    {
      columnId: 'activeIngredientLabels',
      field: 'activeIngredientLabels',
      title: t('timeline.annotationTooltip.activeSubstance'),
      width: 180,
      sorting: true,
      customSort: (a, b) => cmpStringList(a.activeIngredientLabels, b.activeIngredientLabels),
      render: (row) => renderList(row, row.activeIngredientLabels),
    },
  ];

  const columns = allColumns.filter((c) => COLUMN_HAS_DATA[c.columnId](medications));

  /* ----- sort & sort change handler ---------------------------------- */

  const onOrderChange = (field: string, direction: 'asc' | 'desc' | undefined | null) =>
    setSorting({field: field || DEFAULT_SORTING.field, direction: direction ?? undefined});

  const sortedMedications = React.useMemo(() => {
    if (!sorting?.field || !sorting.direction) return medications;
    const column = allColumns.find((c) => c.field === sorting.field);
    if (!column?.customSort) return medications;
    const dir = sorting.direction === 'asc' ? 1 : -1;
    return [...medications].sort((a, b) => column.customSort!(a, b, 'row') * dir);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medications, sorting]);

  return (
    <div css={styles.wrapper} data-name={'medication-placeholder-document'}>
      <LightTable<StructuredMedicationRowDto>
        data={sortedMedications}
        columns={columns}
        isLoading={isLoading}
        controlledSort={sorting}
        onOrderChange={onOrderChange}
        alignTop
        rowsAreSingleLine={false}
        cssVars={{rowHeight: 46}}
        style={{height: '100%'}}
      />
    </div>
  );
};

MedicationPlaceholderDocument.displayName = 'MedicationPlaceholderDocument';

// Silence unused-import warning — kept for future use of isNullsy in column
// visibility checks above (currently we use field-specific helpers instead).
void isNullsy;

const getStyles: GetStylesFunction = (options) => ({
  wrapper: css`
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    width: 100%;
    height: 100%;
    background: #ffffff;
    overflow: hidden;

    /* Force the header row and every data row inside the LightTable to extend
       to their natural content width (sum of column widths). Without this
       override the body rows are stretched only to the scroll container width
       while the header / scroll content extends further — leaving a white gap
       to the right of each row when horizontally scrolled. */
    .MuiTableHead-root,
    & [role='row'] {
      min-width: max-content;
    }
  `,
  ellipsisCell: css`
    display: block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  stackedList: css`
    display: flex;
    flex-direction: column;
    gap: 2px;
    align-items: flex-start;
    max-width: 100%;
  `,
  tooltipWrapper: css`
    display: flex;
    flex-direction: column;
    gap: 8px;
  `,
  tooltipSection: css`
    display: flex;
    flex-direction: column;
    align-items: start;
  `,
  tooltipStack: css`
    display: flex;
    flex-direction: column;
    gap: 2px;
    align-items: flex-start;
  `,
  message: css`
    padding: ${options?.theme?.spacing(4)};
  `,
});
